/**
 * Redesigned Project Card (Specra v2)
 *
 * Human-first: type badge, name, a short description, and a meta footer with
 * last-activity freshness, member count, and archived state. Deliberately does
 * not surface the internal project UUID — it carries no meaning for users.
 * Owner actions (archive/delete) live in the shared ProjectActionsMenu.
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraBadge } from '@mono/ui-components';
import { Clock, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getProjectTypeLabel } from '../../constants/project-types';
import { cn } from '../../lib/utils';
import { getProjectPath } from '../../lib/utils/project-routes';
import { staleness } from '../../utils/time';
import { ProjectActionsMenu } from './ProjectActionsMenu';
const TYPE_BADGE_VARIANT = {
    proposal: 'info',
    contract: 'success',
    general: 'neutral',
    pre_award: 'warning'
};
export function ProjectCardRedesign({ project }) {
    const isArchived = project.status === 'archived';
    const badgeVariant = TYPE_BADGE_VARIANT[project.type] ?? 'neutral';
    const activity = staleness(project.lastActivityAt);
    return /*#__PURE__*/ _jsxs("div", {
        className: "group relative flex flex-col rounded-lg border border-border bg-card transition-colors hover:border-brand",
        children: [
            /*#__PURE__*/ _jsxs(Link, {
                to: getProjectPath(project),
                className: "flex flex-1 flex-col px-[18px] py-4",
                children: [
                    /*#__PURE__*/ _jsx("h3", {
                        className: "pr-7 text-[15px] font-medium tracking-[-0.01em] text-foreground break-words",
                        children: project.name
                    }),
                    project.description ? /*#__PURE__*/ _jsx("p", {
                        className: "mt-1 line-clamp-2 text-[13px] leading-snug text-muted-foreground",
                        children: project.description
                    }) : null,
                    /*#__PURE__*/ _jsxs("div", {
                        className: "mt-auto flex items-center gap-3 pt-4 text-xs text-muted-foreground",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraBadge, {
                                variant: badgeVariant,
                                children: getProjectTypeLabel(project.type)
                            }),
                            /*#__PURE__*/ _jsxs("span", {
                                className: cn('flex items-center gap-1', activity.stale && 'text-warning'),
                                children: [
                                    /*#__PURE__*/ _jsx(Clock, {
                                        className: "size-3 shrink-0"
                                    }),
                                    activity.label
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("span", {
                                className: "flex items-center gap-1",
                                children: [
                                    /*#__PURE__*/ _jsx(Users, {
                                        className: "size-3 shrink-0"
                                    }),
                                    project.memberCount
                                ]
                            }),
                            isArchived ? /*#__PURE__*/ _jsx(SpecraBadge, {
                                variant: "neutral",
                                className: "ml-auto",
                                children: "Archived"
                            }) : null
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "absolute top-3 right-3 z-10",
                children: /*#__PURE__*/ _jsx(ProjectActionsMenu, {
                    project: project,
                    triggerClassName: "opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
                })
            })
        ]
    });
}
